/**
 * Show-Not-Tell slice API client.
 *
 * Implements show-not-tell/TS-004 (T1) and show-not-tell/US-004 (T1).
 * Enforces BR-TS004.2 / BR-009 (slice isolation): this module calls ONLY
 * /sessions/show-not-tell/* endpoints (API-001, API-002, API-003, API-004,
 * API-006). It must NEVER import or call any comprehension /sessions
 * endpoint or service (e.g. services/sessions.ts) — the writing-coach
 * surface stays byte-for-byte separate from the comprehension flow.
 *
 * Modelled on frontend/src/services/mathCoach.ts: thin async functions over
 * the shared `api` Axios instance (frontend/src/lib/api.ts). Errors are NOT
 * caught here — they propagate to the hook layer (useWritingSession, T3)
 * which maps them to gentle copy. A 200-body TURN_DEGRADED response
 * (API-002, degraded:true) is a SUCCESS, not an error — it is returned
 * as-is so the UI renders the degraded coach turn normally (DD-3/DD-005).
 */

import { api } from '@/lib/api';
import type {
  StartWritingResponse,
  RespondWritingResponse,
  CompleteWritingResponse,
  CancelWritingResponse,
  WritingSessionState,
  RecentWritingResponse,
  WritingHistoryResponse,
  ActiveWritingSessionResponse,
} from '@/types/writingPractice';

/**
 * Start (or resume) the one active writing session for the child (API-001).
 * Errors (402/401/403/404/429/500) propagate to the caller — do not catch here.
 */
export async function startWritingSession(starterEmotion?: string): Promise<StartWritingResponse> {
  const body = starterEmotion ? { starterEmotion } : {};
  const { data } = await api.post<StartWritingResponse>('/sessions/show-not-tell/start', body);
  return data;
}

/**
 * Exchange one coaching turn (API-002). The 200 TURN_DEGRADED body is a
 * success — it is returned normally, never thrown.
 */
export async function respondWritingTurn(
  sessionId: string,
  childTurnText: string
): Promise<RespondWritingResponse> {
  const { data } = await api.post<RespondWritingResponse>(
    `/sessions/show-not-tell/${encodeURIComponent(sessionId)}/respond`,
    { childTurnText }
  );
  return data;
}

/** Complete the session — win summary + report + single coin charge (API-003). */
export async function completeWritingSession(sessionId: string): Promise<CompleteWritingResponse> {
  const { data } = await api.post<CompleteWritingResponse>(
    `/sessions/show-not-tell/${encodeURIComponent(sessionId)}/complete`,
    {}
  );
  return data;
}

/** Cancel/abandon the active session and free the one-active slot (API-004). */
export async function cancelWritingSession(sessionId: string): Promise<CancelWritingResponse> {
  const { data } = await api.post<CancelWritingResponse>(
    `/sessions/show-not-tell/${encodeURIComponent(sessionId)}/cancel`,
    {}
  );
  return data;
}

/**
 * Fetch the full owned session (ordered messages + phase) for reload restore
 * (API-006). Used by useWritingSession.restore so a page reload never starts
 * a new session (AC-TS004.4 / AC-004.4).
 */
export async function getWritingSession(sessionId: string): Promise<WritingSessionState> {
  const { data } = await api.get<WritingSessionState>(
    `/sessions/show-not-tell/${encodeURIComponent(sessionId)}`
  );
  return data;
}

/**
 * Fetch the bounded, recency-ordered list of the caller's own completed
 * writing sessions for the dashboard tile (API-005).
 *
 * Implements show-not-tell/TS-012 (T3, AC-TS012.1, AC-TS012.3). Errors are
 * NOT caught here — they propagate to the tile's useQuery (T4), which
 * renders the fail-safe empty/unavailable state (edge case 'Recent read
 * fails during dashboard assembly').
 */
export async function getRecentWritingSessions(): Promise<RecentWritingResponse> {
  const { data } = await api.get<RecentWritingResponse>('/sessions/show-not-tell/recent');
  return data;
}

/**
 * Fetch one keyset-paginated page of the caller's own COMPLETE
 * writing-session history (all statuses, not just completed), ordered
 * most-recent-first (sibling to /recent, API-005).
 *
 * Implements show-not-tell/TS-014 (T3, AC-TS014.1, AC-TS014.6). Errors are
 * NOT caught here — they propagate to the hook layer (useWritingHistory, T4)
 * which renders the retry-able ErrorState (mirrors getRecentWritingSessions'
 * no-try/catch convention).
 */
export async function getWritingHistory(cursor?: string): Promise<WritingHistoryResponse> {
  const { data } = await api.get<WritingHistoryResponse>(
    '/sessions/show-not-tell/history',
    cursor ? { params: { cursor } } : undefined
  );
  return data;
}

/**
 * Fetch a minimal, read-only summary of the caller's own ACTIVE writing
 * session (or null), for the landing page's resume-vs-start decision.
 *
 * Implements BUG-20260718-154634 (C-3). Errors are NOT caught here — they
 * propagate to the hook layer (useWritingHistory), matching this file's
 * existing no-try/catch convention (getRecentWritingSessions,
 * getWritingHistory above).
 *
 * BUG-20260718-172622 (T-6, corrected 2026-07-18): this docstring
 * previously claimed "the backend route itself already degrades a read
 * failure to activeSession: null server-side (TO-LL-036), so a client-side
 * failure here is limited to genuine network errors" — that assumption is
 * FALSE and was disproved by this bug: an API Gateway dev-stage deployment
 * drift silently misrouted this exact endpoint to a different handler,
 * producing a genuine, deterministic 404 that was not a network error and
 * that the backend never had a chance to degrade. A hard failure of this
 * call (routing/infra error, 5xx, etc.) is a real, expected failure mode,
 * not just "genuine network errors" — the hook layer (useWritingHistory)
 * now surfaces it via `activeError` rather than silently treating it as
 * "no active session".
 */
export async function getActiveWritingSession(): Promise<ActiveWritingSessionResponse> {
  const { data } = await api.get<ActiveWritingSessionResponse>('/sessions/show-not-tell/active');
  return data;
}
